import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { CornerDownLeft, Loader2, Mic, Paperclip, X } from 'lucide-react';
// Re-exported so existing importers keep working; the value lives in a module
// nothing mocks — see composerControls.ts for why (#1800).
import { CONTROL_GHOST } from './composerControls';
import { cn } from '@/lib/utils';
import { COARSE_HIT_32 } from '@/lib/touch-target';
import { VoiceButton, type VoiceButtonState } from '@/components/voice/VoiceButton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { COMPANION_NAME } from '@/lib/companion-config';
import { useDictation } from '@/hooks/useDictation';
import type { PendingAttachment, PendingReceipt } from '@/types/chat';
import { MAX_CHAT_MESSAGE_LENGTH } from '@core/chat/constants/message-limits';
import { formatNumber } from '@/i18n/intl';
import { bcp47For } from '@/i18n/negotiate';
import { msg, useMsg, type Msg, type TKey } from '@/i18n/msg';
import { isEnglishUi, useUiLanguage } from '@/features/chat-trf/language';

/** Re-exported for existing importers; the value lives in a module nothing
 *  mocks — see composerControls.ts for why (#1800). */
export { CONTROL_GHOST };

/** #2603: the composer's 32 px ghost controls reach 44 px on a coarse pointer (D-2420). */
const COMPOSER_TOUCH = COARSE_HIT_32;

const noop = () => {};

/** §1.7: a 32px borderless ghost square on the 8px control radius. */

/**
 * Composer attachment capability (docs/architecture/chat-ui-direction.md §7).
 * - `receipt`   — parent flow: photo/PDF receipt → reimbursement mini-form.
 * - `documents` — staff flow: office documents uploaded for text extraction.
 * - `none`      — surfaces with no upload path render no `+` control at all.
 */
export type ChatInputAttachments = 'receipt' | 'documents' | 'none';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  isTyping?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /**
   * Seeds the draft with pre-typed, fully editable text (#929 — the widget
   * prefill). Each distinct non-empty value seeds at most once, and only into
   * an empty draft — it never clobbers what the user already typed and it
   * NEVER sends on its own. The caret lands at the end of the seeded text;
   * focus follows the surface's existing `autoFocus` contract.
   */
  initialValue?: string;
  /** Default 'receipt' — the parent composer keeps today's behaviour untouched. */
  attachments?: ChatInputAttachments;
  pendingReceipt?: PendingReceipt | null;
  /** Chip state for `attachments='documents'`. */
  pendingAttachment?: PendingAttachment | null;
  onFileSelect?: (file: File) => void;
  onRemoveReceipt?: () => void;
  onRemoveAttachment?: () => void;
  /** The PiP machine's state (#1731) or the legacy panel's. */
  voiceState?: VoiceButtonState;
  onVoiceStart?: () => void;
  onVoiceStop?: () => void;
  isVoiceActive?: boolean;
  hideVoiceButton?: boolean;
  /**
   * The draft as it changes — on type, clear, the send that empties it, and a
   * dictation append (2026-09-17). A list rendered outside the composer
   * (`WilliamsIdeas` under the Williams page's composer and the home hero)
   * hides itself on the first character; the composer itself offers no
   * prompts of its own, so it needs no chip lane.
   */
  onDraftChange?: (text: string) => void;
  /**
   * Extra controls for the toolbar's LEFT group, rendered after the attachment
   * clip (#1800). Style them with the exported `CONTROL_GHOST` so they carry the
   * clip's size, tint and hover — a control at a different weight beside it
   * reads as a different kind of thing.
   *
   * The composer does not decide whether these show; the caller does, and passes
   * nothing when they should not.
   */
  leadingActions?: React.ReactNode;
}

// #2109 R3-B4: label maps are key maps, resolved at render.
const RECEIPT_STATUS_LABEL_KEY: Record<PendingReceipt['status'], TKey> = {
  uploading: 'chat:composer.status.uploading',
  submitted: 'chat:composer.status.submitted',
  error: 'chat:composer.status.failed',
};

const RECEIPT_STATUS_VARIANT: Record<PendingReceipt['status'], 'secondary' | 'default' | 'destructive'> = {
  uploading: 'secondary',
  submitted: 'default',
  error: 'destructive',
};

const ATTACHMENT_STATUS_LABEL_KEY: Record<PendingAttachment['status'], TKey> = {
  uploading: 'chat:composer.status.uploading',
  attached: 'chat:composer.status.attached',
  error: 'chat:composer.status.failed',
};

const ATTACHMENT_STATUS_VARIANT: Record<
  PendingAttachment['status'],
  'secondary' | 'default' | 'destructive'
> = {
  uploading: 'secondary',
  attached: 'default',
  error: 'destructive',
};

const RECEIPT_ACCEPT = '.pdf,image/*';
const DOCUMENT_ACCEPT = '.docx,.xlsx,.pptx,.pdf,.png,.jpg,.jpeg,.txt';
const DOCUMENT_EXTENSIONS = ['docx', 'xlsx', 'pptx', 'pdf', 'png', 'jpg', 'jpeg', 'txt'];

/**
 * The `accept` attribute is a filter, not a gate: "All files" in the OS picker,
 * drag-and-drop and some mobile pickers all deliver unsupported types anyway.
 * Rejecting them here — visibly — beats letting the upload 400 later or, worse,
 * dropping the file silently.
 */
function validateDocument(file: File): Msg | null {
  const dot = file.name.lastIndexOf('.');
  const ext = dot > -1 ? file.name.slice(dot + 1).toLowerCase() : '';
  if (ext === 'doc') {
    return msg('chat:composer.fileErrors.legacyDoc');
  }
  if (!ext) {
    return msg('chat:composer.fileErrors.noExtension');
  }
  if (!DOCUMENT_EXTENSIONS.includes(ext)) {
    return msg('chat:composer.fileErrors.unsupported', { ext });
  }
  return null;
}

export function ChatInput({
  onSend,
  disabled = false,
  isTyping = false,
  placeholder,
  autoFocus = false,
  initialValue,
  attachments = 'receipt',
  pendingReceipt,
  pendingAttachment,
  onFileSelect,
  onRemoveReceipt,
  onRemoveAttachment,
  voiceState,
  onVoiceStart,
  onVoiceStop,
  isVoiceActive,
  hideVoiceButton = false,
  onDraftChange,
  leadingActions,
}: ChatInputProps) {
  // The composer mounts inside the open panel, so an `initialValue` present at
  // mount is the per-open prefill seed (#929).
  const [message, setMessage] = useState(initialValue ?? '');
  const { t } = useTranslation('chat');
  const resolve = useMsg();
  const uiLanguage = useUiLanguage();
  const [fileError, setFileError] = useState<Msg | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastSeededRef = useRef(initialValue ?? '');
  const caretToEndRef = useRef(Boolean(initialValue));

  // Every path that writes `message` — the textarea, submit's clear, the
  // prefill seed, a dictation append — reports through one effect, so a
  // caller cannot miss a path. Latest-callback ref: an inline arrow from the
  // parent must not re-fire the report on every render.
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  useEffect(() => {
    onDraftChangeRef.current?.(message);
  }, [message]);

  // A prefill can also arrive while the composer is already mounted (e.g. the
  // Help page's "Ask about this" with the panel open). Seed each distinct
  // value once, only into an empty draft — never clobber the user's typing,
  // and never send.
  useEffect(() => {
    if (!initialValue) {
      // A cleared prefill re-arms seeding so the same text can prefill a
      // later open without remounting.
      lastSeededRef.current = '';
      return;
    }
    if (initialValue === lastSeededRef.current) return;
    lastSeededRef.current = initialValue;
    setMessage((prev) => {
      if (prev.trim()) return prev;
      caretToEndRef.current = true;
      return initialValue;
    });
  }, [initialValue]);

  // Caret to the end of freshly seeded text; focus only where the surface
  // already asks for it (the shell's focus contract keys off `autoFocus`).
  useEffect(() => {
    if (!caretToEndRef.current) return;
    caretToEndRef.current = false;
    const el = textareaRef.current;
    if (!el) return;
    if (autoFocus) el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [message, autoFocus]);

  const isReceiptBlocking = pendingReceipt?.status === 'uploading';
  // Sending before the extraction round-trip finishes would produce an agent
  // turn that can't see the document, so an in-flight upload blocks send too.
  const isAttachmentBlocking = pendingAttachment?.status === 'uploading';
  const effectiveDisabled = disabled || isReceiptBlocking || isAttachmentBlocking;

  // Dictation writes into the same `message` state the user types into, so the
  // transcript is editable and never auto-sends (chat-ui-direction §7).
  const appendTranscript = useCallback((text: string) => {
    setMessage((prev) => (prev.trim() ? `${prev.replace(/\s+$/, '')} ${text}` : text));
  }, []);
  // #2109 R3 L5b: a non-English reader dictates in the page's language; an
  // English reader keeps the browser's own tag, as before.
  const dictation = useDictation({
    onFinalResult: appendTranscript,
    lang: isEnglishUi(uiLanguage) ? undefined : bcp47For(uiLanguage),
  });

  // F-7 (#1731): the length guard — the backend refuses over-long messages
  // (413); the composer says so before the send, and keeps the text editable.
  const messageTooLong = message.trim().length > MAX_CHAT_MESSAGE_LENGTH;

  const submitMessage = () => {
    if (!message.trim() || effectiveDisabled || messageTooLong) return;
    onSend(message.trim());
    setMessage('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); submitMessage(); };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitMessage(); }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset first: re-picking the same file after a rejection must still fire
    // `change`, which it won't if the input still holds that value.
    e.target.value = '';
    if (!file) return;
    if (attachments === 'documents') {
      const rejection = validateDocument(file);
      if (rejection) { setFileError(rejection); return; }
    }
    setFileError(null);
    if (onFileSelect) onFileSelect(file);
  };

  const toggleDictation = () => {
    if (dictation.recording) dictation.stop();
    else dictation.start();
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  return (
    <div className="space-y-2">
      <form onSubmit={handleSubmit} className="relative">
        {attachments !== 'none' && (
          <input
            ref={fileInputRef}
            type="file"
            accept={attachments === 'documents' ? DOCUMENT_ACCEPT : RECEIPT_ACCEPT}
            className="hidden"
            onChange={handleFileChange}
          />
        )}

        {/* Chat/voice parity (#1731, F-7, spec §1.7): one composer grammar for
            the page and the widget — radius/padding/control size and radius
            from the --chat-composer-* / --chat-control-* tokens, a hairline
            border on the card surface, a soft elevation. The textarea is
            chrome-less and opens on two lines; the control row sits under it,
            every control a 32px ghost square on the 8px control radius, send
            = the return glyph (Enter sends, so the button shows the key it
            stands in for) on the solid interaction fill. */}
        <div
          data-testid="composer-surface"
          className="rounded-[var(--chat-composer-radius)] border border-chat-border bg-card p-[var(--chat-composer-pad)] shadow-chat-md"
        >
          {messageTooLong && (
            <p role="alert" className="mb-2 text-caption text-destructive">
              {t('composer.tooLong', {
                length: formatNumber(message.trim().length),
                max: formatNumber(MAX_CHAT_MESSAGE_LENGTH),
              })}
            </p>
          )}

          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder ?? t('composer.placeholder', { companion: COMPANION_NAME })}
              disabled={effectiveDisabled}
              autoFocus={autoFocus}
              aria-label={t('composer.inputLabel')}
              rows={1}
              className={cn(
                // Victor 2026-09-07: one line to start, growing with the text —
                // the two-line minimum left a dead band above the toolbar that
                // read as a second, separate box.
                'min-h-[calc(1*var(--chat-body-lh)*var(--chat-body-size))] max-h-[200px] resize-none',
                'border-0 bg-transparent shadow-none px-0 py-0 text-body text-chat-text',
                'focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none',
              )}
            />
            {(isTyping || (effectiveDisabled && disabled)) && (
              <div className="absolute bottom-1 right-1 flex gap-1">
                {effectiveDisabled && disabled ? (
                  <Loader2 className="h-4 w-4 animate-spin text-stevens-ink-3" />
                ) : (
                  <>
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </>
                )}
              </div>
            )}
          </div>

          {/* #2603 (D-2420): the 32 px icon controls reach 44 px on a coarse
              pointer. Each group is `isolate`, so the -z-10 hit areas sit under
              every drawn control in it, and the coarse gap is 12 px (twice the
              6 px reach) so no two hit areas overlap. */}
          <div className="mt-1.5 flex items-center justify-between" role="toolbar" aria-label={t('composer.toolbarLabel')}>
            <div className="isolate flex items-center gap-1 [@media(pointer:coarse)]:gap-3">
              {/* Attachment — receipt mini-form after file pick (#641), or a
                  document upload on staff surfaces (#901). Not disabled on a
                  prior 'error' chip so the user can retry. */}
              {attachments !== 'none' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={effectiveDisabled}
                      aria-label={attachments === 'documents' ? t('composer.attachDocument') : t('composer.attachReceipt')}
                      className={cn(CONTROL_GHOST, COMPOSER_TOUCH)}
                    >
                      <Paperclip size={16} aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{attachments === 'documents' ? t('composer.attachDocument') : t('composer.attachReceipt')}</TooltipContent>
                </Tooltip>
              )}
              {/* #1800: a rare thread-level action belongs beside the clip, at
                  the clip's weight — not on a row of its own above the quick
                  prompts, where it was the loudest thing in the composer area
                  for something a family reaches for once. The caller owns what
                  goes here and whether it renders at all. */}
              {leadingActions}
            </div>

            <div className="isolate flex items-center gap-1 [@media(pointer:coarse)]:gap-3">
              {/* Dictation (#902) — speech becomes editable text in the box
                  above; it never sends on its own. Browsers without
                  SpeechRecognition get no mic at all rather than a dead
                  affordance (honesty rule). */}
              {dictation.supported && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={toggleDictation}
                        // Stop must stay reachable if the composer disables
                        // mid-dictation — otherwise the mic is stuck listening.
                        disabled={effectiveDisabled && !dictation.recording}
                        aria-pressed={dictation.recording}
                        aria-label={dictation.recording ? t('composer.stopDictation') : t('composer.dictate')}
                        className={cn(
                          CONTROL_GHOST,
                          COMPOSER_TOUCH,
                          // `animate-gentle-breathe` is the repo's own soft pulse
                          // (index.css); the destructive tint carries the state
                          // when motion is reduced.
                          dictation.recording && 'text-destructive animate-gentle-breathe',
                        )}
                      >
                        <Mic size={16} aria-hidden="true" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {dictation.recording ? t('composer.stopDictationHint') : t('composer.dictate')}
                    </TooltipContent>
                  </Tooltip>

                  {dictation.recording && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" onClick={dictation.cancel} aria-label={t('composer.discardDictation')} className={cn(CONTROL_GHOST, COMPOSER_TOUCH)}>
                          <X size={16} aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t('composer.discardDictation')}</TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}

              {/* Voice call — starts/stops the voice session. Keeps its own
                  (round) affordance: it opens a call, it is not a text control. */}
              {!hideVoiceButton && (
                <VoiceButton
                  onStart={onVoiceStart ?? noop}
                  onStop={onVoiceStop ?? noop}
                  isActive={isVoiceActive ?? false}
                  state={voiceState ?? 'idle'}
                />
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="submit"
                    aria-label={t('composer.send')}
                    disabled={effectiveDisabled || message.trim().length === 0 || messageTooLong}
                    className={cn(
                      'ml-1.5 flex h-[var(--chat-control-size)] w-[var(--chat-control-size)] items-center justify-center rounded-[var(--chat-control-radius)] bg-action-fill text-action-ink transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40',
                      COMPOSER_TOUCH,
                    )}
                  >
                    <CornerDownLeft size={16} strokeWidth={2.2} aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('composer.sendHint')}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        {pendingReceipt && (
          <div className="flex items-center gap-2 mt-2 px-1">
            <div className="flex items-center gap-2 bg-muted rounded-full px-3 py-1 text-body max-w-full overflow-hidden">
              <Paperclip className="h-3.5 w-3.5 text-stevens-ink-3 flex-shrink-0" />
              <span className="truncate max-w-[180px]">{pendingReceipt.file.name}</span>
              <Badge variant={RECEIPT_STATUS_VARIANT[pendingReceipt.status]} className="text-caption flex-shrink-0">
                {resolve(msg(RECEIPT_STATUS_LABEL_KEY[pendingReceipt.status]))}
              </Badge>
              <button type="button" onClick={onRemoveReceipt} aria-label={t('composer.removeAttachment')} className="ml-1 hover:text-destructive flex-shrink-0">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {pendingReceipt.status === 'error' && pendingReceipt.error && (
              <span className="text-caption text-destructive truncate">{pendingReceipt.error}</span>
            )}
          </div>
        )}

        {attachments === 'documents' && pendingAttachment && (
          <div className="flex items-center gap-2 mt-2 px-1">
            <div className="flex items-center gap-2 bg-muted rounded-full px-3 py-1 text-body max-w-full overflow-hidden">
              <Paperclip className="h-3.5 w-3.5 text-stevens-ink-3 flex-shrink-0" />
              <span className="truncate max-w-[180px]">{pendingAttachment.file.name}</span>
              <Badge
                variant={ATTACHMENT_STATUS_VARIANT[pendingAttachment.status]}
                className="text-caption flex-shrink-0"
              >
                {resolve(msg(ATTACHMENT_STATUS_LABEL_KEY[pendingAttachment.status]))}
              </Badge>
              <button
                type="button"
                onClick={onRemoveAttachment}
                aria-label={t('composer.removeFile')}
                className="ml-1 hover:text-destructive flex-shrink-0"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {pendingAttachment.status === 'error' && pendingAttachment.error && (
              <span className="text-caption text-destructive truncate">{pendingAttachment.error}</span>
            )}
          </div>
        )}

        {/* Rejected-file and dictation failures are announced, not swallowed. */}
        {fileError && (
          <p role="alert" className="text-caption text-destructive mt-2 px-1">{resolve(fileError)}</p>
        )}
        {dictation.error && (
          <p role="alert" className="text-caption text-destructive mt-2 px-1">{dictation.error}</p>
        )}

      </form>
    </div>
  );
}
